import { NoSsr } from '@cian/react-utils';
import { Image, IconImage16, LinkButton, Responsive, TDeviceType } from '@cian/ui-kit';
import * as React from 'react';

import * as styles from './Card.css';
import { CommercialInfoReadyBusiness } from './internal/components/CommercialInfoReadyBusiness';
import { trackCardClick } from './tracking';
// eslint-disable-next-line import/no-restricted-paths
import { callbackIfIdle } from '../../../browser/utils/callbackIfIdle';
import { ElectronicTradingButtonContainer } from '../../containers/ElectronicTradesButtonContainer';
import { useIsDailyrentOnlineBooking } from '../../hooks/useIsDailyrentOnlineBooking';
import { ECommunicationType } from '../../repositories/favorites/entities/favorite_entity_for_desktop/FavoriteEntityForDesktopSchema';
import { IPageState } from '../../types/page';
import { IFavoriteEntity } from '../../types/results';
import { useApplicationContext } from '../../utils/applicationContext';
import { getDeletedDateString } from '../../utils/favoriteEntity';
import { getIsOfferWithMultiAds } from '../../utils/getIsOfferWithMultiAds';
import { AboutBuilding } from '../AboutBuilding';
import { AboutBuildingMultiAds } from '../AboutBuildingMultiAds';
import { Address } from '../Address';
import { BookButton } from '../BookButton';
import { CardShields } from '../CardShields';
import { Favorites } from '../Favorites';
import { LinkArea } from '../LinkArea';
import { OfferDescription } from '../OfferDescription/OfferDescription';
import { OfferHeader } from '../OfferHeader';
import { OfferHeaderMultiAds } from '../OfferHeaderMultiAds';
import { OnlineBookingBookButton } from '../OnlineBookingBookButton';
import { trackCall, trackPhoneButtonClick } from '../Phone/tracking';
import { PhoneButton } from '../PhoneButton';
import { TabletCardControls } from '../TabletCardControls';
import { Text } from '../Text';
import { Undergrounds } from '../Undergrounds';

export interface ICardProps {
  favoriteEntity: IFavoriteEntity;
  page: IPageState;
  total: number;
  position: number;

  removeFavorite(id: number): void;
}

export const Card: React.FC<ICardProps> = ({ total, favoriteEntity, page, position, removeFavorite }) => {
  const trackMediaClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_photo' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackTitleClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_header' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackJKButtonClick = React.useCallback(() => {
    trackCardClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType, label: 'card_button' });
  }, [favoriteEntity, page.deviceType, position, total]);
  const trackShowNumber = React.useCallback(() => {
    trackPhoneButtonClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
  }, [favoriteEntity, page, position, total]);
  const trackShowNumberMobile = React.useCallback(() => {
    trackPhoneButtonClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
    callbackIfIdle(() => trackCall({ entity: favoriteEntity, position, total, deviceType: page.deviceType }), 10000);
  }, [favoriteEntity, page, position, total]);
  const handleRemove = React.useCallback(() => {
    removeFavorite(favoriteEntity.entityId);
  }, [favoriteEntity.entityId, removeFavorite]);

  const {
    agentInfo,
    title,
    fullUrl,
    externalOfferUrl,
    photoPreviewUrl,
    photoFullUrl,
    bargainTerms,
    jkInfo,
    geo,
    description,
    phone,
    commercialInfo,
    status,
    deleteDate,
    commercialOwnership,
    communicationType = ECommunicationType.Phone,
    titleForMobileSite,
    isCianPartner,
    tradingLink,
  } = favoriteEntity;
  const href = isCianPartner ? (externalOfferUrl as string) : fullUrl;
  const target = isCianPartner ? '_blank' : undefined;

  const hasImage = photoPreviewUrl || photoFullUrl;

  const isMultiAds = getIsOfferWithMultiAds(commercialInfo);
  const isDailyrentOnlineBooking = useIsDailyrentOnlineBooking();

  const { config } = useApplicationContext();
  /**
   * @todo Удалить переменную когда будет удален фича-тогл
   * @description Данный функционал появился в задачах CD-192026 и CD-194164, будет удалён в задаче CD-192195
   */
  const electronicTradingEnabled = config.get<Boolean>('FavoritesMobile.ElectronicTrading.Enabled');
  const shouldShowShieldsUnderTitle = electronicTradingEnabled;
  const isElectronicTradingAvailable = electronicTradingEnabled && !!tradingLink;

  const actionButton = (() => {
    if (isElectronicTradingAvailable) {
      return <ElectronicTradingButtonContainer favoriteEntity={favoriteEntity} deviceType={'phone' as TDeviceType} />;
    }

    if (communicationType === ECommunicationType.Forms) {
      return (
        <LinkButton href={`${fullUrl}?opendealrentform=true`} fullWidth>
          Назначить просмотр
        </LinkButton>
      );
    }

    if (isCianPartner) {
      return (
        <BookButton
          phone={phone}
          favoriteEntity={favoriteEntity}
          page={page}
          total={total}
          size="M"
          position={position}
        />
      );
    }

    if (isDailyrentOnlineBooking) {
      return (
        <OnlineBookingBookButton entity={favoriteEntity} href={href} offersQty={total} position={position} size="L" />
      );
    }

    return <PhoneButton phone={phone} onClick={trackShowNumberMobile} />;
  })();

  return (
    <div className={styles['card']}>
      <LinkArea className={styles['media']} href={href} target={target} onClick={trackMediaClick}>
        <div className={styles['gallery']} data-testid="card_image">
          {hasImage ? (
            <Image
              src={photoPreviewUrl || photoFullUrl || ''}
              height={178}
              alt="Главное фото объявления"
              objectFit="cover"
              borderRadius={0}
            />
          ) : (
            <div className={styles['empty_photo']}>
              <IconImage16 color="gray20_100" />
            </div>
          )}
          {/*
           * @todo Удалить блок с шильдиками и класс shields_wrapper--legacy когда будет удален фича-тогл
           * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192195
           */}
          {!shouldShowShieldsUnderTitle && (
            <div data-testid="card-shields" className={styles['shields_wrapper--legacy']}>
              <CardShields
                agentLists={agentInfo?.agentLists}
                isCommericalOwner={!!commercialOwnership?.isFromOwner}
                isMobile
              />
            </div>
          )}
        </div>
        {status && status !== 'published' && (
          <div className={styles['overlay']}>
            <div className={styles['text']}>
              <Text bold={true}>
                Объявление
                <br />
                снято с публикации
              </Text>
              {deleteDate && (
                <NoSsr>
                  <Text textSize="small" color="black">
                    Объявление в избранном
                    <br />
                    хранится 90 дней,
                    <br />и будет удалено {getDeletedDateString(deleteDate)}
                  </Text>
                </NoSsr>
              )}
            </div>
          </div>
        )}
      </LinkArea>
      <div className={styles['content']}>
        <LinkArea className={styles['content']} href={href} target={target} onClick={trackTitleClick}>
          <div className={styles['header']}>
            {commercialInfo?.readyBusinessType != null ? (
              <div
                data-testid="commercial-info-ready-business-wrapper"
                className={styles['commercial-info-ready-business-wrapper']}
              >
                <CommercialInfoReadyBusiness commercialInfo={commercialInfo} title={title} />
              </div>
            ) : (
              <>
                {isMultiAds && bargainTerms.priceText ? (
                  <OfferHeaderMultiAds>{bargainTerms.priceText}</OfferHeaderMultiAds>
                ) : (
                  bargainTerms.priceText && <OfferHeader price={bargainTerms.priceText} />
                )}
                {/*
                 * @todo Удалить условие shouldShowShieldsUnderTitle когда будет удален фича-тогл
                 * @description Данный функционал появился в задаче CD-192026, будет удалён в задаче CD-192195
                 */}
                {shouldShowShieldsUnderTitle && (
                  <div data-testid="card-shields" className={styles['shields_wrapper']}>
                    <CardShields
                      agentLists={agentInfo?.agentLists}
                      isCommericalOwner={!!commercialOwnership?.isFromOwner}
                      isMobile
                    />
                  </div>
                )}
                {isMultiAds ? (
                  <AboutBuildingMultiAds>{titleForMobileSite}</AboutBuildingMultiAds>
                ) : (
                  <AboutBuilding name={title} buildingInfo={jkInfo?.deadlineText ?? ''} onLinkClick={trackTitleClick} />
                )}
              </>
            )}
            {geo.undergrounds.length ? <Undergrounds undergrounds={geo.undergrounds} /> : null}
            <Address address={geo.address} />
            {description ? <OfferDescription description={description} /> : null}
          </div>
        </LinkArea>
        <Responsive phone>
          <div className={styles['call']}>{actionButton}</div>
        </Responsive>
        <div className={styles['controls']}>
          <TabletCardControls
            favoriteEntity={favoriteEntity}
            communicationType={communicationType}
            phone={phone}
            jkInfo={jkInfo}
            fullUrl={fullUrl}
            trackPhoneClick={trackShowNumber}
            trackJKButtonClick={trackJKButtonClick}
            page={page}
            total={total}
            position={position}
          />
          <div className={styles['favorite']}>
            <Favorites onClick={handleRemove} />
          </div>
        </div>
      </div>
    </div>
  );
};

Card.displayName = 'Card';
